import * as React from 'react'

export interface DeleteDialogContextModel {
  isOpen: boolean
  openDialog: (
    id: string,
    variant: string,
    opts?: { isPostPage?: boolean; redirectTo?: string; onDeleted?: () => void },
  ) => void
  closeDialog: () => void
  targetId: string | null
  variant: 'comment' | 'post' | null
  isPostPage?: boolean
  redirectTo?: string
  onDeleted?: () => void
}

const DeleteDialogContext = React.createContext<DeleteDialogContextModel>(null!)

export const DeleteDialogProvider = ({ children }: React.PropsWithChildren) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false)
  const [targetId, setTargetId] = React.useState<string | null>(null)
  const [variant, setVariant] = React.useState<'comment' | 'post' | null>(null)
  const [isPostPage, setIsPostPage] = React.useState<boolean>(false)
  const [redirectTo, setRedirectTo] = React.useState<string | undefined>()
  const [onDeleted, setOnDeleted] = React.useState<(() => void) | undefined>()

  const openDialog = (
    id: string,
    variant: string,
    opts?: { isPostPage?: boolean; redirectTo?: string; onDeleted?: () => void },
  ) => {
    setIsOpen(true)
    setTargetId(id)
    setVariant(variant as 'comment' | 'post')
    setIsPostPage(Boolean(opts?.isPostPage))
    setRedirectTo(opts?.redirectTo)
    setOnDeleted(() => opts?.onDeleted)
  }

  const closeDialog = () => {
    setIsOpen(false)
    setTargetId(null)
    setVariant(null)
    setIsPostPage(false)
    setRedirectTo(undefined)
    setOnDeleted(undefined)
  }

  return (
    <DeleteDialogContext.Provider
      value={{
        isOpen,
        openDialog,
        closeDialog,
        targetId,
        variant,
        isPostPage,
        redirectTo,
        onDeleted,
      }}
    >
      {children}
    </DeleteDialogContext.Provider>
  )
}

export const useDeleteDialog = () => {
  const context = React.useContext(DeleteDialogContext)

  if (context === undefined) {
    throw new Error('useDeleteDialog must be used within a DeleteDialogProvider')
  }

  return context
}
